import React from 'react';

/**
 * Semantic colour role for a segment. Colours come from theme tokens by design — unlike
 * `color`/`trackColor`, which predate that rule, there is no free-form colour here. To
 * restyle a role, override its token via `applyTheme` / `applyCustomTheme`.
 */
export type ProgressSegmentVariant = 'default' | 'success' | 'warning' | 'error' | 'info';

/** `hatched` reads as claimed-but-unpaid-for — usage past an allowance, not more of it. */
export type ProgressSegmentFill = 'solid' | 'hatched';

export interface ProgressSegment {
    /** Portion of `max` this segment covers, in the same units. */
    value: number;
    variant?: ProgressSegmentVariant;
    fill?: ProgressSegmentFill;
}

interface ProgressCommonProps {
    height?: number;
    'aria-label'?: string;
    'aria-labelledby'?: string;
    /** Spoken instead of the range, which reads as 100% once segments fill the track. */
    'aria-valuetext'?: string;
    className?: string;
    style?: React.CSSProperties;
}

interface ProgressValueProps extends ProgressCommonProps {
    value: number;
    color?: string;
    trackColor?: string;
    segments?: never;
    max?: never;
}

interface ProgressSegmentsProps extends ProgressCommonProps {
    segments: ProgressSegment[];
    /** Scale the segments measure against. Defaults to their sum, and a larger sum wins. */
    max?: number;
    value?: never;
    color?: never;
    trackColor?: never;
}

/** One fill or a list of segments, never both — `color`/`trackColor` mean nothing to segments. */
export type ProgressProps = ProgressValueProps | ProgressSegmentsProps;

const TRACK_CLASSES = 'w-full overflow-hidden bg-[var(--malachite-card-dimmed-background)]';
const FILL_CLASSES =
    'h-full transition-[width] duration-200 ease-in-out bg-[var(--malachite-text-primary)]';
const SEGMENT_CLASSES = 'h-full transition-[width] duration-200 ease-in-out';

const SEGMENT_COLOR: Record<ProgressSegmentVariant, string> = {
    default: 'var(--malachite-text-primary)',
    success: 'var(--malachite-text-success)',
    warning: 'var(--malachite-text-warning)',
    error: 'var(--malachite-text-error)',
    info: 'var(--malachite-text-info)',
};

// Bands sit perpendicular to the gradient axis, so this draws them bottom-left to top-right.
const HATCH_AXIS_ANGLE = '135deg';
const HATCH_STRIPE_PX = 2;
const HATCH_PERIOD_PX = 5;
const HATCH_GAP_COLOR = 'var(--malachite-card-dimmed-background)';

const segmentStyle = (segment: ProgressSegment, widthPercent: number): React.CSSProperties => {
    const color = SEGMENT_COLOR[segment.variant ?? 'default'];
    const base: React.CSSProperties = { width: `${widthPercent}%`, backgroundColor: color };
    if (segment.fill !== 'hatched') {
        return base;
    }
    return {
        ...base,
        backgroundImage: `repeating-linear-gradient(${HATCH_AXIS_ANGLE}, ${color} 0 ${HATCH_STRIPE_PX}px, ${HATCH_GAP_COLOR} ${HATCH_STRIPE_PX}px ${HATCH_PERIOD_PX}px)`,
    };
};

const sumOf = (segments: ProgressSegment[]): number =>
    segments.reduce((total, segment) => total + Math.max(0, segment.value), 0);

const Progress: React.FC<ProgressProps> = ({
    value,
    segments,
    max,
    color,
    trackColor,
    height = 4,
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
    'aria-valuetext': ariaValueText,
    className = '',
    style = {},
}) => {
    const clamped = Math.max(0, Math.min(100, value ?? 0));
    const radius = Math.max(2, Math.round(height / 2));
    const drawn = segments?.filter((segment) => segment.value > 0) ?? [];
    const segmented = segments !== undefined;
    const total = segmented ? sumOf(drawn) : 0;
    // Only guards the division: flooring at 1 unconditionally would rescale a sub-1 max.
    const requestedScale = Math.max(max ?? total, total);
    const scale = requestedScale > 0 ? requestedScale : 1;

    return (
        <div
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={segmented ? scale : 100}
            // Segment totals are arbitrary, so rounding one side could put it past the other.
            aria-valuenow={segmented ? total : Math.round(clamped)}
            aria-valuetext={ariaValueText}
            aria-label={ariaLabel}
            aria-labelledby={ariaLabelledBy}
            className={`${TRACK_CLASSES} ${className}`}
            style={{
                height: `${height}px`,
                borderRadius: `${radius}px`,
                ...(trackColor && !segmented && { background: trackColor }),
                ...style,
            }}
        >
            {segmented ? (
                <div className="flex h-full w-full">
                    {drawn.map((segment, index) => (
                        <div
                            // Segments are a positional list with no identity of their own.
                            key={`${segment.variant ?? 'default'}-${index}`}
                            className={SEGMENT_CLASSES}
                            style={segmentStyle(segment, (segment.value / scale) * 100)}
                        />
                    ))}
                </div>
            ) : (
                <div
                    className={FILL_CLASSES}
                    style={{
                        width: `${clamped}%`,
                        ...(color && { background: color }),
                    }}
                />
            )}
        </div>
    );
};

export default Progress;
export { Progress };
